import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  getClients,
  uploadFile,
  manualEntry,
  getUploads,
  previewUpload,
  confirmUploadMapping,
  getUploadStatus,
  confirmUploadDate,
} from '../utils/api';
import toast from 'react-hot-toast';
import { Upload, FileText, Image, Table, CheckCircle, Clock, XCircle, Plus, Pencil } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const PLATFORMS = ['meta', 'google', 'linkedin', 'twitter', 'tiktok', 'other'];

const fileIcon = (type) => {
  if (type === 'image') return <Image size={14} />;
  if (type === 'pdf') return <FileText size={14} />;
  return <Table size={14} />;
};

const statusBadge = (status) => {
  const map = {
    completed: { label: 'Extracted', cls: 'badge-green', icon: <CheckCircle size={10} /> },
    processing: { label: 'Processing', cls: 'badge-yellow', icon: <Clock size={10} /> },
    mapping_required: { label: 'Review mapping', cls: 'badge-yellow', icon: <Clock size={10} /> },
    date_required: { label: 'Date needed', cls: 'badge-yellow', icon: <Clock size={10} /> },
    pending: { label: 'Pending', cls: 'badge-gray', icon: <Clock size={10} /> },
    failed: { label: 'Failed', cls: 'badge-red', icon: <XCircle size={10} /> },
  };
  const s = map[status] || map.pending;
  return <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>;
};

// Screenshot/PDF uploads process via OCR in the background (no column-mapping
// step to wait on synchronously), so the frontend polls for the result. Capped
// at 40 tries (~2 minutes) so a stuck/slow OCR run doesn't poll forever.
const pollUploadStatus = async (uploadId, { intervalMs = 3000, maxAttempts = 40 } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const status = await getUploadStatus(uploadId);
    if (['completed', 'failed', 'date_required'].includes(status.extraction_status)) {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error('Extraction is taking longer than expected. Check back in a moment.');
};

export default function UploadData() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [platform, setPlatform] = useState('meta');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploads, setUploads] = useState([]);
  const [mode, setMode] = useState('file'); // 'file' | 'manual'
  const [manualForm, setManualForm] = useState({ spend: '', impressions: '', clicks: '', ctr: '', cpc: '', conversions: '', cpa: '', roas: '', revenue: '', campaignName: '' });
  const [mappingModal, setMappingModal] = useState(false);
  const [mappingHeaders, setMappingHeaders] = useState([]);
  const [mappingData, setMappingData] = useState({});
  const [previewUploadId, setPreviewUploadId] = useState(null);
  const [importValidationPreview, setImportValidationPreview] = useState(null);
  const [dateModal, setDateModal] = useState(null); // { uploadId, fileName, metrics, dateRangeHint } | null
  const [dateModalStart, setDateModalStart] = useState('');
  const [dateModalEnd, setDateModalEnd] = useState('');
  const [confirmingDate, setConfirmingDate] = useState(false);

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedClient) {
      getUploads(selectedClient).then(setUploads).catch(() => {});
    }
  }, [selectedClient]);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.xls'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/pdf': ['.pdf'], 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
  });

  const isTabularFile = (file) => /\.(csv|xlsx|xls)$/i.test(file.name);

  const handleUpload = async () => {
    if (!selectedClient) { toast.error('Please select a client'); return; }
    if (!acceptedFiles.length) { toast.error('Please select a file'); return; }
    const ok = window.confirm(
      `You selected "${platform.toUpperCase()}" as the platform.\n\nIs this file really from ${platform.toUpperCase()} Ads?`
    );

    if (!ok) return;

    const file = acceptedFiles[0];
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('clientId', selectedClient);
    fd.append('platform', platform);
    if (dateStart) fd.append('dateRangeStart', dateStart);
    if (dateEnd) fd.append('dateRangeEnd', dateEnd);

    // CSV/Excel go through the column-mapping preview/confirm flow below.
    // Screenshots/PDFs have no columns to map — they're OCR'd in the
    // background, so this branch uploads and then polls for the result
    // instead, prompting for a date range if none could be detected.
    if (!isTabularFile(file)) {
      try {
        const result = await uploadFile(fd, setProgress);
        setUploads(prev => [{ id: result.uploadId, file_name: file.name, file_type: result.fileType, extraction_status: 'processing', created_at: new Date().toISOString(), platform }, ...prev]);
        setUploading(false);
        setProgress(0);

        const status = await pollUploadStatus(result.uploadId);

        if (status.extraction_status === 'date_required') {
          setDateModal({
            uploadId: result.uploadId,
            fileName: file.name,
            metrics: status.extracted_data?.metrics || {},
            dateRangeHint: status.extracted_data?.dateRangeHint || null,
          });
          setDateModalStart(dateStart || '');
          setDateModalEnd(dateEnd || '');
        } else if (status.extraction_status === 'failed') {
          toast.error(status.extraction_error || 'Could not extract data from this file');
        } else {
          trackEvent('data_uploaded', { method: 'screenshot_ocr', platform, file_type: result.fileType });
          toast.success('Data extracted and imported successfully');
        }

        if (selectedClient) getUploads(selectedClient).then(setUploads).catch(() => {});
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || 'Upload failed');
        setUploading(false);
        setProgress(0);
      }
      return;
    }

    try {
      const result = await previewUpload(fd, setProgress);

      setPreviewUploadId(result.uploadId);
      setMappingHeaders(result.headers || []);
      setMappingData(result.suggestedMapping || {});
      setImportValidationPreview(result.importValidationPreview || null);
      setMappingModal(true);

      toast.success('Review column mapping before import');

      setUploads(prev => [{ id: result.uploadId, file_name: file.name, file_type: result.fileType, extraction_status: 'processing', created_at: new Date().toISOString(), platform }, ...prev]);

          } catch (err) {
            toast.error(err.response?.data?.error || 'Upload preview failed');
          } finally {
            setUploading(false);
            setProgress(0);
          }
        };

  const handleConfirmDate = async () => {
    if (!dateModal) return;
    if (!dateModalStart || !dateModalEnd) {
      toast.error('Please select both a start and end date');
      return;
    }
    if (new Date(dateModalEnd) < new Date(dateModalStart)) {
      toast.error('End date must be on or after the start date');
      return;
    }

    setConfirmingDate(true);
    try {
      await confirmUploadDate(dateModal.uploadId, dateModalStart, dateModalEnd);
      trackEvent('data_uploaded', { method: 'screenshot_ocr_date_confirmed', platform });
      toast.success('Data imported successfully');
      setDateModal(null);
      setDateModalStart('');
      setDateModalEnd('');
      if (selectedClient) getUploads(selectedClient).then(setUploads).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to confirm date');
    } finally {
      setConfirmingDate(false);
    }
  };



const handleConfirmMapping = async () => {
  const requiredFields = ['spend', 'revenue', 'conversions'];

  const missingImportant = requiredFields.filter(
    (field) => !mappingData[field] || mappingData[field] === 'ignore'
  );

  if (missingImportant.length > 0) {
    const ok = window.confirm(
      `Important fields are missing: ${missingImportant.join(', ')}.\n\nDo you still want to import?`
    );

    if (!ok) return;
  }

  try {
    setUploading(true);

    await confirmUploadMapping(
      previewUploadId,
      mappingData
    );

 trackEvent('data_uploaded', { method: 'csv_excel', platform });
 toast.success('Data imported successfully');

     setMappingModal(false);
     setPreviewUploadId(null);
     setMappingHeaders([]);
     setMappingData({});
     setImportValidationPreview(null);


    if (selectedClient) {
      const latestUploads = await getUploads(selectedClient);
      setUploads(latestUploads);
    }
  } catch (err) {
    toast.error(
      err.response?.data?.error || 'Import failed'
    );
  } finally {
    setUploading(false);
  }
};

  const setM = (k) => (e) => setManualForm(f => ({ ...f, [k]: e.target.value }));

  const handleManual = async () => {
    if (!selectedClient) { toast.error('Please select a client'); return; }
    if (!dateStart) { toast.error('Please select a report month'); return; }
    try {
      await manualEntry({
        clientId: selectedClient, platform,
        reportMonth: dateStart,
        campaignName: manualForm.campaignName || undefined,
        metrics: Object.fromEntries(Object.entries(manualForm).filter(([k]) => k !== 'campaignName').map(([k, v]) => [k, parseFloat(v) || 0])),
      });
      toast.success('Data saved!');
      setManualForm({ spend: '', impressions: '', clicks: '', ctr: '', cpc: '', conversions: '', cpa: '', roas: '', revenue: '', campaignName: '' });
      if (selectedClient) getUploads(selectedClient).then(setUploads);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div className="page-title">Upload Data</div>
        <div className="page-subtitle">Import ad performance data from files or enter manually</div>
      </div>

      <div className="grid grid-2" style={{ gap: 20, alignItems: 'start' }}>
        {/* Left: Config + Upload */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Config card */}
          <div className="card card-pad">
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Configuration</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Client *</label>
                <select className="form-select" value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Platform *</label>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                  Select the correct ad platform before upload. This affects platform charts and comparisons.
                </div>
                <select className="form-select" value={platform} onChange={e => setPlatform(e.target.value)}>
                  {PLATFORMS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} {p === 'meta' ? 'Ads' : p === 'google' ? 'Ads' : ''}</option>)}
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Date From</label>
                  <input type="date" className="form-input" value={dateStart} onChange={e => setDateStart(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date To</label>
                  <input type="date" className="form-input" value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Mode toggle */}
          <div style={{ display: 'flex', background: 'var(--bg3)', padding: 4, borderRadius: 10, gap: 4 }}>
            {[{ id: 'file', icon: <Upload size={13} />, label: 'Upload File' }, { id: 'manual', icon: <Pencil size={13} />, label: 'Manual Entry' }].map(({ id, icon, label }) => (
              <button key={id} onClick={() => setMode(id)} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: mode === id ? 'var(--bg2)' : 'transparent',
                color: mode === id ? 'var(--text)' : 'var(--text2)',
                boxShadow: mode === id ? 'var(--shadow)' : 'none',
                transition: 'all .15s',
              }}>
                {icon} {label}
              </button>
            ))}
          </div>

          {mode === 'file' ? (
            <div className="card card-pad">
              {/* Dropzone */}
              <div {...getRootProps()} style={{
                border: `2px dashed ${isDragActive ? 'var(--primary)' : 'var(--border2)'}`,
                borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
                background: isDragActive ? 'var(--primary-light)' : 'var(--bg3)',
                transition: 'all .15s', marginBottom: 14,
              }}>
                <input {...getInputProps()} />
                <Upload size={28} style={{ margin: '0 auto 10px', color: isDragActive ? 'var(--primary)' : 'var(--text3)' }} />
                <div style={{ fontWeight: 600, marginBottom: 4, color: isDragActive ? 'var(--primary)' : 'var(--text)' }}>
                  {isDragActive ? 'Drop file here' : 'Drag & drop or click to upload'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>CSV, Excel, PDF, PNG, JPG • Max 50MB</div>
              </div>

              {acceptedFiles.length > 0 && (
                <div style={{ padding: '10px 12px', background: 'var(--success-light)', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={14} color="var(--success)" />
                  <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>{acceptedFiles[0].name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>{(acceptedFiles[0].size / 1024).toFixed(0)} KB</span>
                </div>
              )}

              {uploading && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ height: 4, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: 'var(--primary)', transition: 'width .3s', borderRadius: 4 }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, textAlign: 'center' }}>{progress}% uploaded</div>
                </div>
              )}

              <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !acceptedFiles.length || !selectedClient} style={{ width: '100%', justifyContent: 'center' }}>
                {uploading ? <><span className="spin" style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block' }} /> Processing...</> : <><Upload size={14} /> Upload & Extract</>}
              </button>

              <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
                Supports Meta Ads, Google Ads exports • OCR for screenshots
              </div>
            </div>
          ) : (
            <div className="card card-pad">
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14 }}>Enter Metrics Manually</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Campaign Name (optional)</label>
                  <input className="form-input" placeholder="Summer Sale 2024" value={manualForm.campaignName} onChange={setM('campaignName')} />
                </div>
                <div className="grid grid-2">
                  {[
                    { key: 'spend', label: 'Spend ($)', ph: '1500.00' },
                    { key: 'impressions', label: 'Impressions', ph: '50000' },
                    { key: 'clicks', label: 'Clicks', ph: '1200' },
                    { key: 'ctr', label: 'CTR (%)', ph: '2.4' },
                    { key: 'cpc', label: 'CPC ($)', ph: '1.25' },
                    { key: 'conversions', label: 'Conversions', ph: '85' },
                    { key: 'cpa', label: 'CPA ($)', ph: '17.65' },
                    { key: 'roas', label: 'ROAS', ph: '3.2' },
                    { key: 'revenue', label: 'Revenue ($)', ph: '4800' },
                  ].map(({ key, label, ph }) => (
                    <div key={key} className="form-group">
                      <label className="form-label">{label}</label>
                      <input className="form-input" type="number" step="0.01" placeholder={ph} value={manualForm[key]} onChange={setM(key)} />
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary" onClick={handleManual} disabled={!selectedClient} style={{ justifyContent: 'center' }}>
                  <Plus size={14} /> Save Data
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Recent Uploads */}
        <div className="card">
          <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Recent Uploads</div>
            {selectedClient && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>For selected client</div>}
          </div>
          {!selectedClient ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text3)' }}>
              Select a client to see upload history
            </div>
          ) : uploads.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text3)' }}>
              No uploads yet for this client
            </div>
          ) : (
            <div>
              {uploads.map(u => (
                <div key={u.id} style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)', flexShrink: 0 }}>
                    {fileIcon(u.file_type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.file_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, display: 'flex', gap: 8 }}>
                      <span style={{ textTransform: 'capitalize' }}>{u.platform || 'N/A'}</span>
                      <span>{new Date(u.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {statusBadge(u.extraction_status)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {mappingModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            className="card"
            style={{
              width: 760,
              maxWidth: '95%',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: 18,
            }}
          >
            <div
              className="card-pad"
              style={{
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 900, fontSize: 18 }}>
                  Confirm Column Mapping
                </div>
                <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
                  Review detected columns before importing data
                </div>
              </div>

              <button
                className="btn btn-sm"
                onClick={() => setMappingModal(false)}
              >
                Close
              </button>
            </div>

            <div className="card-pad">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  marginBottom: 12,
                  fontWeight: 800,
                  color: 'var(--text3)',
                  fontSize: 12,
                }}
              >
                <div>System Field</div>
                <div>Excel / CSV Column</div>
              </div>

              {[
                'spend',
                'revenue',
                'conversions',
                'clicks',
                'impressions',
                'reach',
                'followers',
                'ctr',
                'cpc',
                'cpa',
                'roas',
                'campaignName',
                'platform',
              ].map((field) => (
                <div
                  key={field}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      textTransform: 'capitalize',
                    }}
                  >
                    {field}
                  </div>

                  <select
                    className="form-select"
                    value={mappingData[field] || 'ignore'}
                    onChange={(e) =>
                      setMappingData((prev) => ({
                        ...prev,
                        [field]: e.target.value,
                      }))
                    }
                  >
                    <option value="ignore">Ignore</option>
                    {mappingHeaders.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

              {importValidationPreview && (
                <div
                  style={{
                    marginTop: 18,
                    padding: 14,
                    borderRadius: 12,
                    background: importValidationPreview.warningMessage ? '#FFF7ED' : '#EFF6FF',
                    border: `1px solid ${importValidationPreview.warningMessage ? '#FDBA74' : '#BFDBFE'}`,
                    color: 'var(--text2)',
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  <div style={{ fontWeight: 900, color: 'var(--text)', marginBottom: 6 }}>
                    Import Validation Preview
                  </div>
                  <div>Platform: {importValidationPreview.detectedPlatform || 'other'}</div>
                  <div>Report Type: {importValidationPreview.detectedReportType || 'needs_review'}</div>
                  <div>
                    Summary row found: {importValidationPreview.detectedSummaryRowFound ? 'Yes' : 'No'}
                  </div>
                  <div>
                    Difference: {Number(importValidationPreview.differencePercent || 0).toFixed(1)}%
                  </div>
                  {importValidationPreview.warningMessage && (
                    <div style={{ marginTop: 8, fontWeight: 700, color: '#C2410C' }}>
                      {importValidationPreview.warningMessage}
                    </div>
                  )}
                </div>
              )}

              <div
                style={{
                  marginTop: 18,
                  padding: 14,
                  borderRadius: 12,
                  background: 'var(--bg3)',
                  color: 'var(--text2)',
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                Please verify important fields like Spend, Revenue and Conversions.
                Wrong mapping can create incorrect reports.
              </div>

              <div
                style={{
                  marginTop: 20,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 12,
                }}
              >
                <button
                  className="btn btn-sm"
                  onClick={() => setMappingModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleConfirmMapping}
                >
                  Confirm & Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {dateModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div className="card" style={{ width: 480, maxWidth: '95%', borderRadius: 18 }}>
            <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>What period does this cover?</div>
              <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
                We couldn't find a date range in "{dateModal.fileName}" — enter the period this screenshot covers to import it.
              </div>
            </div>

            <div className="card-pad">
              {dateModal.dateRangeHint && (
                <div style={{
                  padding: '10px 12px', borderRadius: 8, background: '#FFF7ED', border: '1px solid #FDBA74',
                  fontSize: 12, color: '#C2410C', marginBottom: 14,
                }}>
                  The image mentions "{dateModal.dateRangeHint}" — enter the actual dates that covers.
                </div>
              )}

              {(dateModal.metrics?.spend || dateModal.metrics?.conversions || dateModal.metrics?.impressions) ? (
                <div style={{
                  padding: '10px 12px', borderRadius: 8, background: 'var(--bg3)', fontSize: 12,
                  color: 'var(--text2)', marginBottom: 14, display: 'flex', gap: 14, flexWrap: 'wrap',
                }}>
                  {dateModal.metrics.spend ? <span>Spend: {dateModal.metrics.spend}</span> : null}
                  {dateModal.metrics.impressions ? <span>Impressions: {dateModal.metrics.impressions}</span> : null}
                  {dateModal.metrics.clicks ? <span>Clicks: {dateModal.metrics.clicks}</span> : null}
                  {dateModal.metrics.conversions ? <span>Conversions: {dateModal.metrics.conversions}</span> : null}
                </div>
              ) : null}

              <div className="grid grid-2" style={{ gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Date From *</label>
                  <input type="date" className="form-input" value={dateModalStart} onChange={e => setDateModalStart(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date To *</label>
                  <input type="date" className="form-input" value={dateModalEnd} onChange={e => setDateModalEnd(e.target.value)} />
                </div>
              </div>

              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button className="btn btn-sm" onClick={() => setDateModal(null)} disabled={confirmingDate}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleConfirmDate} disabled={confirmingDate}>
                  {confirmingDate ? 'Importing...' : 'Confirm & Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
